<script setup lang="ts">
	import Datepicker, { DatePickerInstance } from '@vuepic/vue-datepicker'
	import { Ref, computed, onMounted, ref } from 'vue'

	/** Pasos válidos de `flow` en @vuepic/vue-datepicker. */
	type FlowStep =
		| 'calendar'
		| 'time'
		| 'month'
		| 'year'
		| 'hours'
		| 'minutes'
		| 'seconds'

	interface Props {
		monthPicker?: boolean
		range?: boolean
		maxDate?: any
		minDate?: any
		startDate?: any
		modelValue?: any
		modelType?: string | null
		placeholder?: string
		isRequired?: boolean
		autocomplete?: string
		value?: string
		label?: string
		name?: string
		min?: number
		disabled?: boolean
		format?: string
		previewFormat?: string
		inputClass?: string
		autoApply?: boolean
		enableTimePicker?: boolean
		// Paso del selector de minutos. Con el default de vue-datepicker (1) el
		// usuario elige minuto a minuto; en rentas y traslados basta 15 o 30.
		minutesIncrement?: number
		// Acota la hora seleccionable (p. ej. horario de sucursal).
		minTime?: { hours?: number; minutes?: number }
		maxTime?: { hours?: number; minutes?: number }
		// Secuencia guiada del menú, p. ej. ['calendar', 'time']: al completar el
		// rango salta solo al selector de hora en vez de exigir el icono de reloj.
		flow?: FlowStep[]
		arrowNavigation?: boolean
		position?: 'left' | 'center' | 'right'
		multiCalendars?: boolean
		error?: string | Ref<string>
		preventMinMaxNavigation?: boolean
		allowedDates?: string[] | Date[]
		closeOnClearValue?: boolean
		maxRange?: string | number
		clearOnOpen?: boolean
		onModelChange?: (value: any) => void
		onInput?: (value: any) => void
		handleInternal?: (date: Date | Date[]) => void
		onCleared?: () => void
	}

	interface Emits {
		(event: 'update:modelValue', value: any): void
	}

	const props = withDefaults(defineProps<Props>(), {
		label: '',
		placeholder: 'Seleccione una fecha',
		min: 0,
		value: '',
		autoApply: true,
		enableTimePicker: false,
		// 1 = default de vue-datepicker; no cambia el comportamiento de quien no
		// lo pase (hoteles, vuelos).
		minutesIncrement: 1,
		arrowNavigation: true,
		position: 'center',
		onModelChange: () => null,
		onInput: () => null,
		modelType: 'format',
		format: 'yyyy-MM-dd',
		previewFormat: 'yyyy-MM-dd',
	})
	const emits = defineEmits<Emits>()
	const pickerRef = ref<DatePickerInstance>(null)

	const onOpen = () => {
		props.clearOnOpen && pickerRef.value?.clearValue()

		setTimeout(() => {
			const menu = document.querySelector('.dp__menu[role="dialog"]')
			menu?.setAttribute(
				'aria-label',
				props.label ? `Calendario: ${props.label}` : 'Calendario'
			)
		}, 0)
	}

	const inputClassName = computed(() => {
		return `py-2 px-8 !rounded-md w-full h-[44px] !border-2 !border-gray-300 !border-[#e5e7eb] !text-sm ${props.inputClass} focus:!border-gray-700 focus:outline-none ${props.disabled ? 'cursor-not-allowed' : ''}`
	})

	interface Expose {
		pickerRef: Ref<DatePickerInstance | null>
	}

	defineExpose<Expose>({
		pickerRef,
	})
</script>

<script lang="ts">
	export default {
		name: 'StyledDateInput',
	}
</script>

<template>
	<div class="grid gap-1">
		<span
			v-if="label"
			class="font-medium text-base"
		>
			{{ label }}
			<span
				v-if="isRequired"
				class="text-red-500"
				>*</span
			>
		</span>

		<Datepicker
			:required="isRequired"
			:aria-labels="{
				input: label || 'Seleccionar fecha',
				menu: 'Calendario',
				nextMonth: 'Mes siguiente',
				prevMonth: 'Mes anterior',
				nextYear: 'Año siguiente',
				prevYear: 'Año anterior',
				openYearsOverlay: 'Seleccionar año',
				openMonthsOverlay: 'Seleccionar mes',
			}"
			ref="pickerRef"
			:model-type="modelType ?? undefined"
			:format="format"
			:preview-format="previewFormat"
			:clearable="true"
			:range="range"
			:model-value="modelValue"
			:min-date="minDate"
			:max-date="maxDate"
			:allowed-dates="allowedDates"
			:disabled="disabled"
			:start-date="maxDate"
			:placeholder="placeholder"
			:auto-apply="autoApply"
			:enable-time-picker="enableTimePicker"
			:minutes-increment="minutesIncrement"
			:min-time="minTime"
			:max-time="maxTime"
			:flow="flow"
			:arrow-navigation="arrowNavigation"
			:input-class-name="inputClassName"
			:position="position"
			:multi-calendars="multiCalendars"
			:month-change-on-arrows="true"
			:prevent-min-max-navigation="preventMinMaxNavigation"
			:close-on-clear-value="closeOnClearValue"
			:max-range="maxRange"
			:no-disabled-range="true"
			:monthPicker="monthPicker"
			locale="es"
			@update:model-value="emits('update:modelValue', $event)"
			@internal-model-change="handleInternal"
			@open="onOpen"
			:teleport="true"
		/>

		<span
			v-if="error"
			class="!text-sm text-red-500"
			>{{ error }}</span
		>
	</div>

	<!-- dp__outer_menu_wrap dp--menu-wrapper -->
</template>

<style scoped>
	.dp__flex_display {
		display: flex;
		gap: 15px;
	}
</style>
